// Video attachments in chat and the gallery: up to 3 minutes. The backend stores
// whatever the browser sends and the media encoder takes any format; a file it
// cannot read is settled as `failed` and shown as a plain file card. The format
// and size limits are switched off for now (null) rather than deleted, so they
// can come back by setting them again.

export const MAX_VIDEO_BYTES = null as null | number;
export const MAX_VIDEO_SECONDS = 180;

/** Formats a pick is held to; null takes any video. */
const ACCEPTED_TYPES = null as null | readonly string[];

/**
 * What the pickers accept. The camera input asks for `video/*` too: a narrower
 * accept makes some Androids skip the camera app altogether, and the recorded
 * clip is validated on the way back like any other pick.
 */
export const VIDEO_ACCEPT = ACCEPTED_TYPES ? ACCEPTED_TYPES.join(',') : 'video/*';

const VIDEO_EXTENSION = /\.(mp4|m4v|mov|qt|webm|mkv|avi|wmv|flv|3gp|3g2|mpe?g|ogv)$/i;

/** Any video attachment, whatever the format — used to pick a renderer. */
export const isVideoAttachment = (type?: string, ...names: (string | undefined)[]) => {
  if (type === 'video' || type?.startsWith('video/')) return true;
  return names.some((name) => Boolean(name && VIDEO_EXTENSION.test(name)));
};

/** Only the formats this feature accepts — used to gate a pick. */
const isAcceptedVideo = (file: File) =>
  !ACCEPTED_TYPES ||
  ACCEPTED_TYPES.includes(file.type) ||
  (!file.type && VIDEO_EXTENSION.test(file.name));

export const formatDuration = (seconds: number) => {
  const whole = Math.round(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

/**
 * Duration of a local file, read from a detached <video>. Resolves to null when
 * the browser cannot parse the container (an iPhone .mov on a codec it lacks,
 * a truncated file) — the caller treats that as "cannot verify the limit".
 */
export const readVideoDuration = (file: File): Promise<number | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const element = document.createElement('video');
    element.preload = 'metadata';
    element.muted = true;

    const finish = (duration: number | null) => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(duration);
    };

    // A file the decoder chokes on can leave both events unfired.
    const timer = setTimeout(() => finish(null), 10000);

    element.onloadedmetadata = () =>
      finish(Number.isFinite(element.duration) && element.duration > 0 ? element.duration : null);
    element.onerror = () => finish(null);

    element.src = url;
  });

/** `duration` is null when this browser cannot read the file — it is still attached. */
export type VideoCheck = { ok: true; duration: number | null } | { ok: false; reason: string };

export const validateVideoFile = async (file: File): Promise<VideoCheck> => {
  if (!isAcceptedVideo(file)) {
    return { ok: false, reason: 'Couldn’t attach this video format. Please try another.' };
  }

  if (MAX_VIDEO_BYTES !== null && file.size > MAX_VIDEO_BYTES) {
    return {
      ok: false,
      reason: `Couldn’t attach videos over ${Math.round(MAX_VIDEO_BYTES / 1024 / 1024)} MB. Please trim it.`,
    };
  }

  // A codec this browser lacks, or a damaged file, is left to the encoder: it
  // either makes a playable copy or the clip is shown as a file to download.
  const duration = await readVideoDuration(file);

  if (duration !== null && duration > MAX_VIDEO_SECONDS) {
    return { ok: false, reason: 'Couldn’t attach videos over 3 minutes. Please trim it.' };
  }

  return { ok: true, duration };
};

/**
 * First frame of a local file as a data URL, for the composer preview. Null
 * when the frame cannot be grabbed — the preview falls back to a plain tile.
 */
export const captureVideoPoster = (file: File): Promise<string | null> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const element = document.createElement('video');
    element.preload = 'metadata';
    element.muted = true;
    element.playsInline = true;

    const finish = (poster: string | null) => {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(poster);
    };

    const timer = setTimeout(() => finish(null), 10000);

    element.onloadeddata = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 480 / (element.videoWidth || 480));
        canvas.width = Math.max(1, Math.round((element.videoWidth || 480) * scale));
        canvas.height = Math.max(1, Math.round((element.videoHeight || 270) * scale));
        canvas.getContext('2d')?.drawImage(element, 0, 0, canvas.width, canvas.height);
        finish(canvas.toDataURL('image/jpeg', 0.7));
      } catch {
        finish(null);
      }
    };
    element.onerror = () => finish(null);

    element.src = url;
    // Nudge past the first frame: seeking to 0 can hand back a black frame.
    element.currentTime = 0.1;
  });

/**
 * `dto.MediaFile` has no duration field, so a video's length is only known to
 * the client that read it — from the file it just picked, or from a clip it has
 * already played. Cache it by name + size (both survive the round trip) so the
 * duration overlay can be drawn without downloading the file again.
 */
const durationCache = new Map<string, number>();

const cacheKey = (name: string, size?: number) => `${name}|${size ?? ''}`;

export const rememberVideoDuration = (
  name: string,
  size: number | undefined,
  seconds: number | null,
) => {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) return;
  durationCache.set(cacheKey(name, size), seconds);
};

export const recallVideoDuration = (name: string, size?: number) =>
  durationCache.get(cacheKey(name, size)) ?? null;
